import { defineStore } from 'pinia';
import { catchError, defer, finalize, firstValueFrom, forkJoin, of, switchMap, tap } from 'rxjs';
import { useMerchantStore } from '~/store/merchant';
import { useUserStore } from '~/store/user';
import { auth } from '~/support/constants';
import { LoginResponse, RefreshResponse } from '~/types';

export const useAuthStore = defineStore('auth', () => {
  const { identity } = useGateway();
  const authenticated = useCookie<boolean>(auth.cookies.AUTHENTICATED) ?? false;
  const refreshToken = useCookie(auth.cookies.REFRESH_TOKEN);
  const accessToken = ref<string | null>(null);
  const merchant = useMerchantStore();
  const user = useUserStore();

  function clearUser() {
    authenticated.value = false;
    refreshToken.value = null;
    accessToken.value = null;
  }

  function handleAuthResponse(res: LoginResponse | RefreshResponse) {
    accessToken.value = res.access_token;

    if ('refresh_token' in res) {
      refreshToken.value = res.refresh_token;
    }

    authenticated.value = true;
  }

  const refresh$ = defer(() => {
    return identity.auth.refresh(refreshToken.value as string).pipe(
      tap(res => {
        if (res?.data) {
          handleAuthResponse(res.data);
        }
      }),
    );
  });

  const logout$ = defer(() => {
    if (!refreshToken.value) {
      return of(true);
    }

    return identity.auth.logout(refreshToken.value as string).pipe(
      finalize(() => {
        clearUser();
      }),
    );
  });

  async function refresh() {
    if (!refreshToken.value) {
      clearUser();

      return await firstValueFrom(of(null));
    }

    return await firstValueFrom(
      refresh$.pipe(
        catchError(error => {
          console.error('unable to refresh token', error);
          clearUser();

          return of(null);
        }),
        tap(res => {
          if (res?.data) {
            handleAuthResponse(res.data);

            return res.data;
          }
        }),
      ),
    );
  }

  function login(credentials: { email: string; password: string }) {
    return identity.auth.login(credentials.email, credentials.password).pipe(
      tap(res => {
        if (res?.data) {
          handleAuthResponse(res.data);
        }
      }),
      switchMap(() => {
        return postLogin();
      }),
    );
  }

  function logout() {
    return logout$;
  }

  function postLogin() {
    return forkJoin([merchant.loadStores(), user.fetchUserInfo()]);
  }

  return {
    accessToken,
    authenticated,
    login,
    logout,
    postLogin,
    refresh,
  };
});
